import { useQuery } from "@tanstack/react-query";
import type {
  AgentTask,
  AgentTaskPhase,
  RunListEntry,
  RunListFilter,
  RunState,
} from "@zworks/console-contracts";
import * as React from "react";
import { Link, useSearchParams } from "react-router";
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { CertaintyBadge, PageHeader } from "@/components/bespoke";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApiError, agentTaskOptionsQuery, agentTasksQuery, runsQuery } from "@/lib/api";
import { formatCredits, formatDate, formatZonedDateTime } from "@/lib/format";
import { useCurrentProject } from "@/lib/project";
import { PhaseBadge, StartForm } from "./agent-tasks";
import { RunStateBadge } from "@/components/bespoke";

const activePhases: AgentTaskPhase[] = ["starting", "running", "idle", "recovering"];

const filters: { value: RunListFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "waiting", label: "Waiting" },
  { value: "running", label: "Running" },
  { value: "unknown", label: "Unknown" },
  { value: "failed", label: "Failed" },
];

const typeFilters = [
  { value: "all", label: "All" },
  { value: "run", label: "Runs" },
  { value: "agent", label: "Agent tasks" },
] as const;

type ListType = (typeof typeFilters)[number]["value"];

function parseListType(value: string | null): ListType {
  return value === "run" || value === "agent" || value === "all" ? value : "all";
}

function creditCell(entry: { creditSpend: number | null; creditCertainty: string }) {
  if (entry.creditCertainty === "unknown") {
    return (
      <span className="flex items-center justify-end gap-1.5 tabular-nums">
        {entry.creditSpend !== null ? formatCredits(entry.creditSpend) : "?"}
        <CertaintyBadge certainty="unknown" />
      </span>
    );
  }
  if (entry.creditCertainty === "estimated") {
    return (
      <span className="flex items-center justify-end gap-1.5 tabular-nums">
        {entry.creditSpend !== null ? formatCredits(entry.creditSpend) : "?"}
        <CertaintyBadge certainty="estimated" />
      </span>
    );
  }
  return (
    <span className="tabular-nums">
      {entry.creditSpend !== null ? formatCredits(entry.creditSpend) : "–"}
    </span>
  );
}

function TypeBadge({ type }: { type: "run" | "agent" }) {
  return (
    <Badge variant={type === "run" ? "info" : "secondary"}>
      {type === "run" ? "Run" : "Agent task"}
    </Badge>
  );
}

/** One row in the unified list: a flow run or an agent task, newest first. */
type UnifiedRow =
  | { kind: "run"; id: string; startedAt: string; entry: RunListEntry }
  | { kind: "agent"; id: string; startedAt: string; task: AgentTask };

function mergeRows(runs: RunListEntry[], tasks: AgentTask[]): UnifiedRow[] {
  const rows: UnifiedRow[] = [
    ...runs.map((entry): UnifiedRow => ({
      kind: "run",
      id: entry.id,
      startedAt: entry.startedAt,
      entry,
    })),
    ...tasks.map((task): UnifiedRow => ({
      kind: "agent",
      id: task.id,
      startedAt: task.createdAt,
      task,
    })),
  ];
  rows.sort((a, b) => (a.startedAt < b.startedAt ? 1 : a.startedAt > b.startedAt ? -1 : 0));
  return rows;
}

function promptSummary(prompt: string): string {
  const firstLine = prompt.split("\n", 1)[0] ?? "";
  const trimmed = firstLine.trim() === "" ? prompt.trim() : firstLine;
  return trimmed.length > 80 ? `${trimmed.slice(0, 80)}…` : trimmed;
}

/**
 * The runs page, as the Runs board draws it: a type filter across runs and agent tasks, the
 * state tabs, one table newest first, and page controls. A row never shows success before it
 * is confirmed, and unknown spend stays visible as unknown, never zero. Detail routes stay
 * per type: a run opens under /runs/:id, an agent task under /agent-tasks/:id.
 */
export function RunsPage() {
  const { project, loaded } = useCurrentProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const listType = parseListType(searchParams.get("type"));
  const [filter, setFilter] = React.useState<RunListFilter>("all");
  const [page, setPage] = React.useState(1);
  const enabled = loaded && project !== null;
  const showRuns = listType !== "agent";
  const showAgents = listType !== "run";
  const list = useQuery({
    ...runsQuery(project?.id ?? "", filter, page),
    enabled: enabled && showRuns,
  });
  const tasks = useQuery({
    ...agentTasksQuery(project?.id ?? ""),
    enabled: enabled && showAgents,
    refetchInterval: (query) =>
      query.state.data?.some((task) => activePhases.includes(task.phase)) ? 15_000 : false,
  });
  const options = useQuery({
    ...agentTaskOptionsQuery(project?.id ?? ""),
    enabled: enabled && showAgents,
  });
  const canWrite = project?.role === "owner" || project?.role === "developer";

  function selectFilter(next: RunListFilter) {
    setFilter(next);
    setPage(1);
  }

  function selectType(next: ListType) {
    setSearchParams(next === "all" ? {} : { type: next }, { replace: true });
    setPage(1);
  }

  const pending = (showRuns && list.isPending) || (showAgents && tasks.isPending);
  // A disabled query stays pending, so the empty project is decided before the query.
  if (!loaded || (project !== null && pending)) {
    return (
      <>
        <PageHeader title="Runs" />
        <LoadingState label="Loading runs" rows={6} />
      </>
    );
  }

  if (project === null) {
    return (
      <>
        <PageHeader title="Runs" />
        <EmptyState
          title="No projects yet"
          description="Create your first project. Its runs appear here."
        />
      </>
    );
  }

  const runsError = showRuns ? list.error : null;
  if (runsError) {
    const error = runsError;
    return (
      <>
        <PageHeader title="Runs" description={project.name} />
        {error instanceof ApiError && error.status === 403 ? (
          <ForbiddenState
            title="You do not have access to this project"
            description="Ask an owner to invite you. Nothing from the project is shown."
          />
        ) : (
          <ErrorState
            title="Runs could not be loaded"
            description={
              error instanceof ApiError
                ? (error.problem?.detail ?? error.message)
                : "The request could not be completed."
            }
            requestId={error instanceof ApiError ? (error.requestId ?? "unknown") : "unknown"}
            onRetry={() => void list.refetch()}
          />
        )}
      </>
    );
  }

  // The project switch can disable the query mid-flight, so data is read once it exists.
  const runsData = showRuns ? list.data : null;
  if (showRuns && !runsData) {
    return (
      <>
        <PageHeader title="Runs" description={project.name} />
        <LoadingState label="Loading runs" rows={6} />
      </>
    );
  }

  const runs = runsData?.runs ?? [];
  const agentList = tasks.data ?? [];
  const agentFailed = tasks.isError && !tasks.data;
  const totalPages = Math.max(1, Math.ceil((runsData?.total ?? 0) / (runsData?.pageSize ?? 20)));
  const currentPage = Math.min(page, totalPages);
  const from =
    (runsData?.total ?? 0) === 0 ? 0 : (currentPage - 1) * (runsData?.pageSize ?? 20) + 1;
  const to = (currentPage - 1) * (runsData?.pageSize ?? 20) + runs.length;
  const rows = mergeRows(runs, agentList);
  const agentOptions = options.data;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Runs"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button className="min-h-11 sm:min-h-9" asChild>
              <Link to="/zot">New task</Link>
            </Button>
            <Button variant="outline" className="min-h-11 sm:min-h-9" asChild>
              <Link to="/runs/new">Start run</Link>
            </Button>
          </div>
        }
      />

      <div role="group" aria-label="Filter by type" className="flex flex-wrap gap-2">
        {typeFilters.map((entry) => (
          <Button
            key={entry.value}
            variant={listType === entry.value ? "default" : "outline"}
            size="sm"
            className="min-h-11 sm:min-h-9"
            aria-pressed={listType === entry.value}
            onClick={() => selectType(entry.value)}
          >
            {entry.label}
          </Button>
        ))}
      </div>

      {showRuns ? (
        <Tabs value={filter} onValueChange={(value) => selectFilter(value as RunListFilter)}>
          <TabsList
            aria-label="Filter runs by state"
            className="max-w-full flex-wrap sm:flex-nowrap"
          >
            {filters.map((entry) => (
              <TabsTrigger
                key={entry.value}
                value={entry.value}
                className="h-11 px-2 sm:h-7.5 sm:px-3"
              >
                {entry.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      ) : null}

      {listType === "agent" && project && agentOptions && canWrite ? (
        <StartForm
          projectId={project.id}
          repositories={agentOptions.repositories}
          branchPrefix={agentOptions.branchPrefix}
          modelKeys={agentOptions.modelKeys ?? []}
        />
      ) : null}

      {agentFailed && (listType !== "run" || rows.length > 0) ? (
        <p role="status" className="text-sm text-muted-foreground">
          Agent tasks could not be loaded right now. Runs are shown below.
        </p>
      ) : null}

      {rows.length === 0 ? (
        <EmptyState
          title={
            listType === "agent"
              ? "No agent tasks yet"
              : filter === "all"
                ? "No runs yet"
                : `No ${filter} runs`
          }
          description={
            listType === "agent"
              ? "Tasks you start appear here."
              : filter === "all"
                ? "Start the first run against this project. It shows Pending until confirmed."
                : "Nothing in this state. Try another filter."
          }
        />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="responsive-table w-full border-collapse text-sm">
              <caption className="sr-only">Runs and agent tasks, newest first</caption>
              <thead>
                <tr>
                  {[
                    "Type",
                    "Run",
                    "Work item",
                    "Flow",
                    "State",
                    "Progress",
                    "Started by",
                    "Started",
                  ].map((header) => (
                    <th
                      key={header}
                      scope="col"
                      className="h-10 border-b px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                    >
                      {header}
                    </th>
                  ))}
                  <th
                    scope="col"
                    className="h-10 border-b px-3 text-right text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                  >
                    Credits
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) =>
                  row.kind === "run" ? (
                    <tr key={`run-${row.id}`} className="group hover:bg-muted/50">
                      <td
                        data-label="Type"
                        className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5"
                      >
                        <TypeBadge type="run" />
                      </td>
                      <td
                        data-label="Run"
                        className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 font-mono text-[0.8125rem]"
                      >
                        <Link
                          to={`/runs/${row.entry.id}`}
                          className="text-accent-text underline-offset-4 hover:underline"
                        >
                          {row.entry.id.slice(0, 8)}
                        </Link>
                      </td>
                      <td
                        data-label="Work item"
                        className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5"
                      >
                        {row.entry.workItemTitle ?? "–"}
                      </td>
                      <td
                        data-label="Flow"
                        className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 font-mono text-[0.8125rem]"
                      >
                        {row.entry.flowVersion ?? "–"}
                      </td>
                      <td
                        data-label="State"
                        className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5"
                      >
                        <RunStateBadge state={row.entry.state as RunState} />
                      </td>
                      <td
                        data-label="Progress"
                        className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            role="progressbar"
                            aria-valuenow={row.entry.progress}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label={`Run progress ${row.entry.progress} per cent`}
                            className="h-1.5 w-20 overflow-hidden rounded-full bg-muted"
                          >
                            <div
                              className="h-full rounded-full bg-primary"
                              style={{ width: `${row.entry.progress}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {row.entry.progress}%
                          </span>
                        </div>
                      </td>
                      <td
                        data-label="Started by"
                        className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5"
                      >
                        {row.entry.startedBy}
                      </td>
                      <td
                        data-label="Started"
                        className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 tabular-nums"
                      >
                        {formatDate(row.entry.startedAt)}
                      </td>
                      <td
                        data-label="Credits"
                        className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 text-right"
                      >
                        {creditCell(row.entry)}
                      </td>
                    </tr>
                  ) : (
                    <tr key={`agent-${row.id}`} className="group hover:bg-muted/50">
                      <td
                        data-label="Type"
                        className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5"
                      >
                        <TypeBadge type="agent" />
                      </td>
                      <td
                        data-label="Run"
                        className="max-w-sm border-b px-3 py-2.5 align-middle group-last:border-b-0 first:pl-5 last:pr-5"
                      >
                        <Link
                          to={`/agent-tasks/${row.task.id}`}
                          className="font-medium text-accent-text underline-offset-4 hover:underline"
                        >
                          {promptSummary(row.task.prompt)}
                        </Link>
                      </td>
                      <td
                        data-label="Work item"
                        className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 font-mono text-[0.8125rem]"
                      >
                        {row.task.repositoryName}
                      </td>
                      <td
                        data-label="Flow"
                        className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 font-mono text-[0.8125rem]"
                      >
                        {row.task.branch}
                      </td>
                      <td
                        data-label="State"
                        className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5"
                      >
                        <PhaseBadge phase={row.task.phase} />
                      </td>
                      <td
                        data-label="Progress"
                        className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5"
                      >
                        –
                      </td>
                      <td
                        data-label="Started by"
                        className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5"
                      >
                        –
                      </td>
                      <td
                        data-label="Started"
                        className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 text-xs text-muted-foreground tabular-nums"
                      >
                        {formatZonedDateTime(row.task.createdAt, "UTC")}
                      </td>
                      <td
                        data-label="Credits"
                        className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 text-right text-muted-foreground"
                      >
                        –
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-3 border-t px-5 py-3">
            <p className="text-[0.8125rem] text-muted-foreground tabular-nums">
              {showRuns ? (
                <>
                  {from}–{to} of {runsData?.total ?? 0} runs
                  {showAgents
                    ? ` · ${agentList.length} agent ${agentList.length === 1 ? "task" : "tasks"}`
                    : null}
                </>
              ) : (
                <>
                  {agentList.length} agent {agentList.length === 1 ? "task" : "tasks"}
                </>
              )}
            </p>
            {showRuns ? (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="min-h-11 sm:min-h-9"
                  disabled={currentPage <= 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="min-h-11 sm:min-h-9"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            ) : null}
          </div>
        </Card>
      )}
    </div>
  );
}
